/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Input } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadDeliverStatusReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";

import { showErrorMessage } from "../../../../utils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { DELIVERY_REPORT_STATUS_LIST } from "../../../../constants/shipmentStatus";
import { useCopilotReadable } from "@copilotkit/react-core";

const DeliverStatusReport = () => {
  const { createNewRequest, isLatestRequest } = useRequestCancellation();
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "days"),
    moment()
  ]);
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(["DELIVERED", "RTO_DELIVERED"]);
  const [awbSearch, setAwbSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  // Make report data available to CopilotKit
  useCopilotReadable({
    description: "Delivery Status Report data including delivery tracking, status updates, and performance metrics",
    value: {
      reportType: "Delivery Status Report",
      reportData: {
        totalRecords: pagination.total,
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        recordsShown: reportData.length,
        data: reportData.slice(0, 10).map(item => ({
          awbNumber: item.awbNumber,
          deliveryDate: item.deliveryDate,
          status: item.status,
          originHub: item.originHub,
          destinationHub: item.destinationHub,
          deliveryStatus: item.deliveryStatus,
          customerName: item.customerName
        }))
      },
      appliedFilters: {
        dateRange: dateRange ? {
          startDate: dateRange[0]?.format("YYYY-MM-DD"),
          endDate: dateRange[1]?.format("YYYY-MM-DD")
        } : null,
        selectedHubs: selectedHubs,
        selectedStatus: selectedStatus,
        awbSearch: awbSearch
      },
      availableOptions: {
        hubList: hubList.map(hub => ({ value: hub.value, label: hub.label })),
        statusOptions: DELIVERY_REPORT_STATUS_LIST
      },
      reportMetrics: {
        loading: loading,
        downloadLoading: downloadLoading,
        userRole: hubUser,
        lastUpdated: new Date().toISOString()
      }
    }
  });

  //add
  // Fetch hub list on mount
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch {
        message.error("Failed to fetch hub list");
      } finally {
        // setLoading(false);
      }
    })();
  }, []);



  // Set default hub for HUBADMIN users
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo"));
    let userid = JSON.parse(localStorage.getItem("userId"));
    setHubUser(user?.roles?.[0]);

    const activeHub = JSON.parse(localStorage.getItem("active_hub"));
    const activeHubId = activeHub?.hubId;

    if (hubList.length > 0) {
      if (activeHubId) {
        const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
        }
      } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
        const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
        }
      }
    }
  }, [hubList]);

  const calendarRef = useRef();

  // Custom handler for date range change
  const handleDateRangeChange = (value) => {
    if (!value || !value[0] || !value[1]) {
      // If cleared, reset to today and open calendar
      setDateRange([moment(), moment()]);
      // Open the calendar dropdown
      if (calendarRef.current && calendarRef.current.picker) {
        calendarRef.current.picker.open();
      }
    } else {
      setDateRange(value);
    }
  };

  // Update useEffect to guard against invalid dateRange
  useEffect(() => {
    if (!hubUser) return;
    if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) return;
    const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
    if (!hasValidDates) return;
    fetchReport();
    // eslint-disable-next-line
  }, [hubUser, selectedHubs, selectedStatus, awbSearch, dateRange, pagination.current, pagination.pageSize]);

  const fetchReport = async () => {
    // Guard against invalid dateRange
    const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
    if (!hasValidDates) {
      return;
    }

    // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
    const { signal, requestId } = createNewRequest("delivery-status-report");

    try {
      setLoading(true);
      setReportData([]); // Clear previous data

      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));

      const params = {
        ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
        ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
        ...(hasValidDates && {
          fromDate: dateRange[0].format("YYYY-MM-DD"),
          toDate: dateRange[1].format("YYYY-MM-DD")
        }),
        ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
        ...(awbSearch.trim() && { awbSearch: awbSearch.trim() }),
        page: (pagination.current - 1), // Convert to 0-based
        size: pagination.pageSize
      };

      // Call the delivery status report API with signal
      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getDeliverStatusReport(params, { signal });

      // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        console.log(`🔥 Request ${requestId} completed successfully - updating state`);
        setReportData(data);
        setLastsynced(lastRefreshedAt);
        setNextScheduledRefresh(nextScheduledRefresh);
        setPagination(prev => ({
          ...prev,
          total,
        }));
      }

    } catch (error) {
      // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled at HTTP level - no state update");
        return;
      }

      // Only update if this is still the latest request
      if (isLatestRequest(requestId)) {
        showErrorMessage("Failed to fetch report data");
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } finally {
      // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
    if (!hasValidDates) {
      showErrorMessage("Please select a date range for download");
      return;
    }
    setDownloadLoading(true);
    try {
      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));

      const downloadParams = {
        ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
        ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
        ...(hasValidDates && {
          fromDate: dateRange[0].format("YYYY-MM-DD"),
          toDate: dateRange[1].format("YYYY-MM-DD")
        }),
        ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
        ...(awbSearch.trim() && { awbSearch: awbSearch.trim() })
      };


      console.log("downloadParams (DeliverStatus click):", selectedHubData.map(hub => hub.value).join(","));




      await downloadDeliverStatusReport(downloadParams);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const navigate = useNavigate();



  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading
          title="Delivery Status Report"
        >
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From & To Date</div>
            <CalendarComponent
              ref={calendarRef}
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From Hub</div>
            <DropDownComponent
              className="small-dropdown"
              placeholder="Origin Hub"
              options={hubList}
              onChange={(value) => {
                setSelectedHubs(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedHubs}
              allowClear
              mode={hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR" ? "multiple" : "default"}
              disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
              maxTagCount="responsive"
              showSearch={true}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">AWB Search</div>
            <Input
              className="small-dropdown"
              placeholder="Enter AWB numbers (comma separated)"
              value={awbSearch}
              onChange={(e) => {
                setAwbSearch(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              maxLength={200}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Status</div>
            <DropDownComponent
              placeholder="Select Status"
              options={DELIVERY_REPORT_STATUS_LIST}
              onChange={(value) => {
                setSelectedStatus(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              value={selectedStatus}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloadLoading}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Col>
        </Row>
      </Card>
      <TableComponent
        columns={[
          { title: "Latest Status Date", dataIndex: "latestStatusDate", key: "latestStatusDate", sorter: true, align: "center", },
          {
            title: "Awb Number", dataIndex: "awbNumber", key: "awbNumber", sorter: true, align: "center",
            render: (text, record) => {
              return (
                <span
                  style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => {
                    const url = `/shipment-tracker?awb=${text}`;
                    window.open(url, "_blank");
                  }}
                >
                  {text}
                </span>
              );
            }
          },
          { title: "From Premise", dataIndex: "fromPremise", key: "fromPremise", sorter: true, align: "center", },
          { title: "No Of Attempts", dataIndex: "noOfAttempts", key: "noOfAttempts", sorter: true, align: "center", },
          { title: "Status", dataIndex: "status", key: "status", sorter: true, align: "center", },
          { title: "Process", dataIndex: "process", key: "process", sorter: true, align: "center", },
          { title: "Current Location", dataIndex: "currentLocation", key: "currentLocation", sorter: true, align: "center", },



        ]}
        dataSource={reportData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="lcrNum"
        style={{ marginTop: 32 }}
      />
    </div>
  );
};

export default DeliverStatusReport;
